import dynamic from "next/dynamic";
import {
  countrySlugsQuery,
  featureSlugsQuery,
  postQuery,
  postSlugsQuery,
  landingPagesQuery,
} from "@/lib/queries";
import { sanityClient, getClient, overlayDrafts } from "@/lib/sanity.server";
import {
  getShippingCost,
  getShippingRatesComparison,
  getRatesForGenericCountryPage,
} from "@/lib/db";
import countriesList from "@/data/countries.json";
import { EXCLUDED_COUNTRIES } from "@/lib/constants";
const BlogPost = dynamic(() => import("@/components/blog-post"));
const CountryPage = dynamic(() => import("@/components/country-page"));
const CountryPageGeneric = dynamic(() =>
  import("@/components/country-page-generic")
);
const FeaturePage = dynamic(() => import("@/components/feature-page"));

export default function Page(props) {
  if (props.type === "post") {
    return <BlogPost {...props} />;
  } else if (props.pageContent.category === "countries") {
    return <CountryPage {...props} />;
  } else if (props.pageContent.category === "generic-country") {
    return <CountryPageGeneric {...props} />;
  } else {
    return <FeaturePage {...props} />;
  }
}

export async function getStaticPaths() {
  let countryWithDedicatedPageSlugs = await sanityClient.fetch(
    countrySlugsQuery
  );
  const allCountrySlugs = countriesList
    .filter((country) => !EXCLUDED_COUNTRIES.includes(country.isoCode))
    .map((country) => `shipping-singapore-to-${country.urlName}`);
  const featureSlugs = await sanityClient.fetch(featureSlugsQuery);
  const postSlugs = await sanityClient.fetch(postSlugsQuery);
  const slugs = [
    ...countryWithDedicatedPageSlugs,
    ...allCountrySlugs,
    ...featureSlugs,
  ];
  // only add post slugs in production, for speeding up preview builds
  if (process.env.NEXT_PUBLIC_ENV === "production") slugs.push(...postSlugs);
  return {
    paths: slugs.map((slug) => ({
      params: { slug },
    })),
    fallback: false,
  };
}

export async function getStaticProps({ params, preview = false }) {
  let countryWithDedicatedPageSlugs = await sanityClient.fetch(
    countrySlugsQuery
  );
  let slug = params.slug;
  if (
    slug.startsWith("shipping-singapore-to-") &&
    !countryWithDedicatedPageSlugs.includes(slug)
  ) {
    slug = "shipping-singapore-to-generic-country";
  }
  const { post, morePosts } = await getClient(preview).fetch(postQuery, {
    slug: slug,
  });
  if (post) {
    const blogMetaContent = overlayDrafts(
      await getClient(preview).fetch(landingPagesQuery, { slug: "blog" })
    )[0];

    return {
      props: {
        blogMetaContent,
        preview,
        data: {
          post,
          morePosts: overlayDrafts(morePosts),
        },
        type: "post",
      },
    };
  } else {
    const pageContent = overlayDrafts(
      await getClient(preview).fetch(landingPagesQuery, {
        slug: slug,
      })
    )[0];
    let props = { pageContent, preview, type: "page" };
    if (pageContent.category === "countries") {
      props = await getCountryPageProps(props);
    } else if (pageContent.category === "generic-country") {
      props = await getGenericCountryPageProps(props, params.slug);
    }
    return { props: props };
  }
}

const getCountryPageProps = async (props) => {
  const { pageContent } = props;
  const isoCode = pageContent.country;
  const countryName = countriesList.find(
    (country) => country.isoCode === isoCode
  ).name;
  const comparison = await getShippingRatesComparison(isoCode);

  // overwrite justship cost
  // TODO: clean this when it's clearer what weights we want to display
  const jsCost = await getShippingCost(0, isoCode, 2);
  comparison["JustShip"] = {
    "2kg": jsCost,
    duration: "2 - 5",
  };
  pageContent.segments.forEach((segment) => {
    if (segment.name.includes("Get Started"))
      segment.blocks[0].heading[0].children[0].text += ` ${countryName}`;
  });
  return { ...props, isoCode, comparison, countryName };
};

const getGenericCountryPageProps = async (props, slug) => {
  const urlName = slug.split("shipping-singapore-to-")[1];
  const countryData = countriesList.find(
    (country) => country.urlName === urlName
  );
  const countryName = countryData.name;
  const isoCode = countryData.isoCode;
  const rates = await getRatesForGenericCountryPage(isoCode);
  return { ...props, isoCode, rates, countryName };
};
